// utils/OrganizationPreloader.js
import { useEffect, useRef } from 'react';
import { useUser } from '../../auth/hooks/useUser';
import organizationCache from '../cache/OrganizationCache';
import OrganizationService from '../services/OrganizationService';
import { sessionManager } from '../../../shared/services/sessionManagement/sessionManager';
import { useProjectsStore } from '../../../store/projects/projectsStore';
import { useDashboardStore } from '../../../store/dashboard/dashboardStore';
import { useDashboardDataStore } from '../../../store/dashboard/dashboardDataStore';
import { useThemeStore } from '../../../store/theme/themeStore';
import { getThemeNameFromHex } from '../../../store/theme/themeConstants';
import { TestService } from '../../../shared/middleware/services/TestService';
import { CollaboratorService } from '../../projects/services/CollaboratorService';
import { clearAllCache } from '../../../shared/config/cachedAxiosConfig';
import logger from '../../../shared/services/logging/logger';

/**
 * Fire-and-forget: warm projects, collaborator data (favourites/owners), and
 * the first project's test runs so the user sees data immediately on the Dashboard.
 * Everything else (members, divisions, groups, etc.) loads lazily on first page visit
 * and is cached by OrganizationCache for subsequent visits.
 */
async function preloadDashboardData(userData) {
  try {
    // 0. Flush all axios HTTP caches before loading.
    //    After logout → re-login the in-memory axios-cache-interceptor
    //    may still hold the previous user's responses (5-min TTL).
    //    Clearing here guarantees a fresh API call for the new user.
    clearAllCache();

    // 1. Force-load projects from API (bypass Zustand + axios caches)
    const { loadProjects } = useProjectsStore.getState();
    await loadProjects(userData, true);

    // 2. Load collaborator data (owner, favourite) for all ACTIVE projects.
    //    Populates the shared projectsStore.collaboratorData so both
    //    DashboardScreen and ProjectView read from a single source of truth.
    const allProjects = useProjectsStore.getState().projects;
    const activeProjects = Array.isArray(allProjects)
      ? allProjects.filter(p => {
          const status = p.status ? p.status.toUpperCase() : 'ACTIVE';
          return status === 'ACTIVE';
        })
      : [];

    // Signal immediately — ProjectView fetches permissions on-demand in batches
    // rather than waiting for all N projects to be pre-fetched upfront.
    if (activeProjects.length > 0 && userData?.accessToken) {
      useProjectsStore.getState().setPermissionsPreloading(false);
      useProjectsStore.getState().setPermissionsPreloaded(true);
    }

    // 3. Determine which project to warm test runs for:
    //    prefer last-used, fall back to most-recently-updated ACTIVE project
    let targetProjectId = useDashboardStore.getState().selectedDashboardProject;

    if (!targetProjectId && activeProjects.length > 0) {
      const sorted = [...activeProjects].sort((a, b) => {
        const aDate = a.updated_date ? new Date(a.updated_date).getTime() : 0;
        const bDate = b.updated_date ? new Date(b.updated_date).getTime() : 0;
        return bDate - aDate;
      });
      targetProjectId = sorted[0]?.id || sorted[0]?.name;
    }

    // 4. Warm test runs for the target project
    if (targetProjectId && userData?.accessToken) {
      const { getTestRuns, setTestRuns } = useDashboardDataStore.getState();
      if (!getTestRuns(targetProjectId)) {
        const runs = await TestService.getRecentRunsByProject(targetProjectId, userData, 20);
        if (Array.isArray(runs)) {
          setTestRuns(targetProjectId, runs);
        }
      }
    }
  } catch {
    // Preload is best-effort — silently ignore failures
  }
}

/**
 * Valid theme names that the app supports.
 * If the API returns a value not in this set (e.g. an unrecognised hex code),
 * the theme defaults to 'purple'.
 */
const VALID_THEME_NAMES = new Set(['purple', 'green', 'blue', 'red', 'yellow']);

/**
 * Fetch the org's colour theme from the API and apply it to the theme store.
 * Theme is NOT persisted — it lives only in Zustand memory for the current session.
 *
 * The API may return either a colour name ('yellow') or a legacy hex code ('#d8b732').
 * Valid names are applied directly; hex codes are overwritten to 'purple'.
 * Unrecognised values also fall back to 'purple'.
 */
async function preloadOrgTheme(orgId) {
  try {
    // Custom colours disabled — clear any leftover custom theme
    try {
      localStorage.removeItem('ecoh-custom-theme');
      const currentTheme = useThemeStore.getState().themeName;
      if (currentTheme?.startsWith('custom:')) {
        useThemeStore.getState().setTheme('purple');
      }
    } catch { /* ignore */ }

    const result = await OrganizationService.getTheme(orgId);
    if (result?.success) {
      const rawTheme = result.data?.orgTheme || result.data?.theme;
      if (rawTheme) {
        // If it's a hex code, overwrite to 'purple' on the server and locally.
        // Otherwise validate it's a known name; fall back to 'purple' if not.
        let themeName;
        if (rawTheme.startsWith('#')) {
          // Map hex → name (e.g. #4B89E7 → 'blue'); unknown hex falls back to 'purple'
          themeName = getThemeNameFromHex(rawTheme);
          // Persist the corrected name so the API no longer stores a hex code
          OrganizationService.updateTheme(orgId, themeName).catch(() => {});
        } else {
          themeName = VALID_THEME_NAMES.has(rawTheme.toLowerCase())
            ? rawTheme.toLowerCase()
            : 'purple';
        }

        const currentTheme = useThemeStore.getState().themeName;
        // Skip if nothing changed — avoids an unnecessary DOM re-application.
        // Also don't let 'purple' (the API's default/fallback) override a theme
        // the user explicitly chose; only a non-purple API value can override.
        if (themeName !== currentTheme && (themeName !== 'purple' || currentTheme === 'purple')) {
          useThemeStore.getState().setTheme(themeName);
        }
      }
    }
  } catch {
    // Best-effort — theme defaults to 'purple' on fresh load
  }
}

/**
 * OrganizationPreloader — a render-less component mounted once after login.
 *
 * On mount it fires two parallel background tasks:
 *   1. preloadDashboardData — projects + collaborator data + test runs
 *   2. preloadOrgTheme      — org colour from API → themeStore
 *
 * Theme is fetched from the API on every login/refresh and lives only in
 * Zustand memory (not persisted to localStorage). This ensures each user
 * always sees their org's current theme without stale data leaking across sessions.
 *
 * All other org data (members, divisions, groups, roles, invitations) loads lazily
 * when the user navigates to those pages and is cached by OrganizationCache so that
 * subsequent visits are instant without needing a global preload.
 */
const OrganizationPreloader = () => {
  const { userData } = useUser();
  const preloadAttemptedRef = useRef(false);
  const dashboardPreloadedRef = useRef(false);
  const currentOrgIdRef = useRef(null);

  const getCurrentTenantId = () => {
    const possibleIds = [
      userData?.tenantId,
      userData?.currentOrganization?.tenantId,
      userData?.organizations?.[0]?.tenantId,
      userData?.currentOrganization?.id,
      userData?.organizations?.[0]?.id
    ];
    return possibleIds.find(id => id !== null && id !== undefined);
  };

  // Main preload effect — fires once per login (or org switch)
  useEffect(() => {
    const tryPreload = async () => {
      const isSessionActive = sessionManager.isSessionActive();
      const hasValidAuth = userData && userData.userId && userData.accessToken;

      if (!hasValidAuth || !isSessionActive) {
        preloadAttemptedRef.current = false;
        dashboardPreloadedRef.current = false;
        currentOrgIdRef.current = null;
        return;
      }

      const orgId = getCurrentTenantId();
      if (!orgId) return;

      // Org switch — reset so new org's data is loaded
      if (currentOrgIdRef.current && currentOrgIdRef.current !== orgId) {
        preloadAttemptedRef.current = false;
        dashboardPreloadedRef.current = false;
        organizationCache.invalidateOrganization(currentOrgIdRef.current);
      }

      if (preloadAttemptedRef.current && currentOrgIdRef.current === orgId) return;

      preloadAttemptedRef.current = true;
      currentOrgIdRef.current = orgId;

      // Fire both background tasks in parallel — neither blocks navigation.
      // preloadDashboardData hits division-scoped endpoints (projects/folders etc.) which the
      // backend rejects with a 403 "missing division claim" for a user with no current
      // division (e.g. just added to the tenant, not yet added to any division) — skip it
      // entirely for that case instead of firing a doomed request. The dashboard itself
      // already renders NoDivisionAccess for these users; theme preload below still runs
      // since it's tenant-scoped, not division-scoped.
      if (!dashboardPreloadedRef.current && userData.divisionId) {
        dashboardPreloadedRef.current = true;
        setTimeout(() => preloadDashboardData(userData), 0);
      }

      // Fetch org theme from the API — this is the single source of truth.
      // Theme is NOT persisted to localStorage; it lives only in Zustand memory
      // so each login/refresh gets a fresh theme from the org's server setting.
      preloadOrgTheme(orgId);
    };

    tryPreload();
  }, [userData]);

  // Logout cleanup — clear org cache, project store, and reset refs
  // so stale data from the previous user's session doesn't leak to the next login.
  useEffect(() => {
    const resetPreloaderState = () => {
      organizationCache.clearAll();
      useProjectsStore.getState().clearCache();
      preloadAttemptedRef.current = false;
      dashboardPreloadedRef.current = false;
      currentOrgIdRef.current = null;
    };

    // 'storage' event fires in OTHER tabs when localStorage changes
    const handleStorageChange = (e) => {
      if (e.key === 'userData' && !e.newValue) {
        resetPreloaderState();
      }
    };

    // 'userLoggedOut' fires in THIS tab during the logout flow
    const handleLoggedOut = () => {
      resetPreloaderState();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLoggedOut', handleLoggedOut);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLoggedOut', handleLoggedOut);
    };
  }, []);

  return null;
};

/**
 * Utility: manually trigger an org theme refresh (e.g. after the user changes their org theme in settings)
 */
export const refreshOrgTheme = async (orgId) => {
  if (!orgId) return;
  await preloadOrgTheme(orgId);
};

/**
 * Utility: clear the OrganizationCache for a specific org (or all orgs)
 */
export const clearOrganizationCache = (orgId = null) => {
  if (orgId) {
    organizationCache.invalidateOrganization(orgId);
  } else {
    organizationCache.clearAll();
  }
};

/**
 * @deprecated — now just refreshes the org theme.
 * Kept for backward compatibility with existing callers/tests.
 */
export const triggerOrganizationPreload = async (orgId) => {
  if (!orgId) return null;
  await preloadOrgTheme(orgId);
  return { success: true };
};

/**
 * @deprecated — now clears cache and refreshes the org theme.
 * Kept for backward compatibility with existing callers/tests.
 */
export const refreshOrganizationCache = async (orgId) => {
  if (!orgId) return null;
  clearOrganizationCache(orgId);
  await preloadOrgTheme(orgId);
  return { success: true };
};

export default OrganizationPreloader;
